import { userManager } from '$lib/auth/oidc';
import { redirect } from '@sveltejs/kit';
import type { ApiResult } from '$lib/types';
import { extractErrorMessage } from '$lib/utils/error/errorUtils';

export async function fetchWithAuth(input: RequestInfo, init?: RequestInit): Promise<Response> {
  const request = new Request(input, init);
  const modifiedRequest = await addAuthToken(request);
  const result = await fetch(modifiedRequest);

  if (result.status === 401) {
    await userManager.removeUser();
    redirect(401, '');
  }

  return result;
}

async function addAuthToken(request: Request): Promise<Request> {
  // The backend validates the OIDC ID token, so send that as the bearer token.
  const user = await userManager.getUser();
  const token = user?.id_token;
  if (token) {
    request.headers.set('Authorization', `Bearer ${token}`);
  }
  return request;
}

// Helper function to extract error message from the new middleware format
export async function extractApiError(response: Response): Promise<string> {
  return extractErrorMessage(response, `Request failed with status ${response.status}`);
}

export const httpUtil = {
  async get<T>(url: string): Promise<ApiResult<T>> {
    try {
      const response = await fetchWithAuth(url);
      
      if (!response.ok) {
        const errorMessage = await extractApiError(response);
        return {
          success: false,
          errors: [errorMessage],
        };
      }
      
      const data = await response.json();
      return {
        success: true,
        data,
      };
    } catch {
      return {
        success: false,
        errors: ['Network error occurred'],
      };
    }
  },

  async post<T>(url: string, body: Record<string, unknown>): Promise<ApiResult<T>> {
    try {
      const response = await fetchWithAuth(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      
      if (!response.ok) {
        const errorMessage = await extractApiError(response);
        return {
          success: false,
          errors: [errorMessage],
        };
      }
      
      const data = await response.json();
      return {
        success: true,
        data,
      };
    } catch {
      return {
        success: false,
        errors: ['Network error occurred'],
      };
    }
  },

  async put<T>(url: string, body: Record<string, unknown>): Promise<ApiResult<T>> {
    try {
      const response = await fetchWithAuth(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      
      if (!response.ok) {
        const errorMessage = await extractApiError(response);
        return {
          success: false,
          errors: [errorMessage],
        };
      }
      
      const data = await response.json();
      return {
        success: true,
        data,
      };
    } catch {
      return {
        success: false,
        errors: ['Network error occurred'],
      };
    }
  },

  async delete<T>(url: string): Promise<ApiResult<T>> {
    try {
      const response = await fetchWithAuth(url, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        const errorMessage = await extractApiError(response);
        return {
          success: false,
          errors: [errorMessage],
        };
      }
      
      // Handle empty responses for DELETE requests
      const text = await response.text();
      const data = text ? JSON.parse(text) : null;
      
      return {
        success: true,
        data,
      };
    } catch {
      return {
        success: false,
        errors: ['Network error occurred'],
      };
    }
  },
};
